import { Card, CardContent, Input, DatePicker } from '../../../components/ui';
import type { DealItem } from '../types/microsite.types';

interface DealsSectionProps {
    dealsEnabled: boolean;
    onDealsEnabledChange: (enabled: boolean) => void;
    deals?: DealItem[];
    onDealsChange: (deals: DealItem[]) => void;
}

export default function DealsSection({
    dealsEnabled,
    onDealsEnabledChange,
    deals = [],
    onDealsChange,
}: DealsSectionProps) {
    const handleAddDeal = () => {
        const newDeal: DealItem = {
            giftItem: '',
            limitedOffer: false,
            spendAmount: 0,
        };
        onDealsChange([...deals, newDeal]);
    };

    const handleRemoveDeal = (index: number) => {
        onDealsChange(deals.filter((_, i) => i !== index));
    };

    const handleUpdateDeal = (
        index: number,
        field: keyof DealItem,
        value: string | number | boolean | Date | undefined
    ) => {
        const updatedDeals = deals.map((deal, i) =>
            i === index ? { ...deal, [field]: value } : deal
        );
        onDealsChange(updatedDeals);
    };

    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-[#e2e8f0]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#ec4899] to-[#f472b6] flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-lg">6</span>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-[#1e293b]">
                                Deals & Gift Offers
                            </h3>
                            <p className="text-sm text-[#64748b]">
                                Offer gifts based on customer spend thresholds
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onDealsEnabledChange(!dealsEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            dealsEnabled ? 'bg-[#6e64f0]' : 'bg-[#cbd5e1]'
                        }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                dealsEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                    </button>
                </div>

                {dealsEnabled && (
                    <div className="space-y-4">
                        {deals.length === 0 ? (
                            <div className="text-center py-8 border-2 border-dashed border-[#cbd5e1] rounded-lg">
                                <p className="text-[#64748b] mb-4">No deals added yet</p>
                                <button
                                    type="button"
                                    onClick={handleAddDeal}
                                    className="px-6 py-2.5 bg-gradient-to-r from-[#6e64f0] to-[#8b7cf6] text-white rounded-lg hover:shadow-lg transition-all font-medium"
                                >
                                    + Add First Deal
                                </button>
                            </div>
                        ) : (
                            <>
                                {deals.map((deal, index) => (
                                    <div
                                        key={index}
                                        className="p-6 border border-[#e2e8f0] rounded-lg space-y-4 bg-[#fafbfc]"
                                    >
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-semibold text-[#1e293b]">
                                                Deal #{index + 1}
                                            </h4>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveDeal(index)}
                                                className="p-1.5 text-[#ef4444] hover:bg-[#fee2e2] rounded transition-colors"
                                            >
                                                <svg
                                                    className="w-5 h-5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="space-y-4">
                                            {/* Gift Item & Spend Amount - Two Column */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <Input
                                                    label="Gift Item"
                                                    type="text"
                                                    value={deal.giftItem || ''}
                                                    onChange={(e) =>
                                                        handleUpdateDeal(
                                                            index,
                                                            'giftItem',
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Free wine glass"
                                                    maxLength={100}
                                                    required
                                                />

                                                <Input
                                                    label="Min Spend (₹)"
                                                    type="number"
                                                    value={deal.spendAmount || ''}
                                                    onChange={(e) =>
                                                        handleUpdateDeal(
                                                            index,
                                                            'spendAmount',
                                                            Number(e.target.value)
                                                        )
                                                    }
                                                    placeholder="5000"
                                                    min={0}
                                                />
                                            </div>

                                            {/* Limited Offer Toggle & Validity - Two Column */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-medium text-[#1e293b]">
                                                        Limited Offer
                                                    </label>
                                                    <div className="flex items-center gap-3 h-[42px]">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                handleUpdateDeal(
                                                                    index,
                                                                    'limitedOffer',
                                                                    !deal.limitedOffer
                                                                )
                                                            }
                                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                                                deal.limitedOffer
                                                                    ? 'bg-[#6e64f0]'
                                                                    : 'bg-[#cbd5e1]'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                                    deal.limitedOffer
                                                                        ? 'translate-x-6'
                                                                        : 'translate-x-1'
                                                                }`}
                                                            />
                                                        </button>
                                                        <span className="text-sm text-[#64748b]">
                                                            {deal.limitedOffer
                                                                ? 'Limited time'
                                                                : 'Always available'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <DatePicker
                                                    label="Valid Until (Optional)"
                                                    value={deal.validity}
                                                    onChange={(date) =>
                                                        handleUpdateDeal(index, 'validity', date)
                                                    }
                                                    placeholder="Select date"
                                                    minDate={new Date()}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={handleAddDeal}
                                    className="w-full px-4 py-3 border-2 border-dashed border-[#e2e8f0] rounded-lg text-[#64748b] hover:border-[#6e64f0] hover:text-[#6e64f0] transition-all flex items-center justify-center gap-2"
                                >
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4v16m8-8H4"
                                        />
                                    </svg>
                                    <span className="font-medium">Add Another Deal</span>
                                </button>
                            </>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
